import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export interface CartProduct {
  id: string;
  name: string;
  nameEn?: string;
  price: number;          // preț fără TVA
  currency: 'RON' | 'EUR';
  quantity: number;
  category: string;
  requiresShipping: boolean;
  dimensions?: { l: number; w: number; h: number }; // cm
  weight?: number; // kg
  imageUrl?: string;
  description?: string;
}

export interface CartState {
  items: CartProduct[];
  totalRon: number;       // total fără TVA, în RON
  totalTva: number;       // TVA 21%
  totalCuTva: number;     // total cu TVA inclus
  totalItems: number;
  eurRate: number;        // cursul EUR/RON folosit (BT VINDEM +2%)
}

interface CartCtx {
  cart: CartState;
  addToCart: (product: Omit<CartProduct, 'quantity'>) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  updateItem: (id: string, updates: Partial<CartProduct>) => void;
  clearCart: () => void;
  isInCart: (id: string) => boolean;
}

/* ──────────────────────────────────────────────────────────────────────────
   Curs EUR: Banca Transilvania VINDEM + 2%
   BT VINDEM EUR = 5.2600 (citit live de pe bancatransilvania.ro/curs-valutar)
   +2% → 5.2600 × 1.02 = 5.3652
   Fallback static dacă API-ul BT nu este accesibil
────────────────────────────────────────────────────────────────────────── */
const BT_SELL_EUR = 5.2600;
const EUR_MARKUP   = 1.02;           // +2% față de VINDEM BT
const EUR_TO_RON   = parseFloat((BT_SELL_EUR * EUR_MARKUP).toFixed(4)); // 5.3652

/* TVA Romania */
const TVA_RATE = 0.21; // 21%

function calcState(items: CartProduct[], eurRate = EUR_TO_RON): CartState {
  const totalRon = items.reduce(
    (s, i) => s + i.price * i.quantity * (i.currency === 'EUR' ? eurRate : 1),
    0
  );
  const totalTva    = totalRon * TVA_RATE;
  const totalCuTva  = totalRon + totalTva;
  const totalItems  = items.reduce((s, i) => s + i.quantity, 0);
  return { items, totalRon, totalTva, totalCuTva, totalItems, eurRate };
}

const CartContext = createContext<CartCtx>({
  cart: { items: [], totalRon: 0, totalTva: 0, totalCuTva: 0, totalItems: 0, eurRate: EUR_TO_RON },
  addToCart: () => {}, removeFromCart: () => {}, updateQuantity: () => {},
  updateItem: () => {}, clearCart: () => {}, isInCart: () => false,
});

const LS_CART     = 'interpersonal_cart';
const LS_EUR_RATE = 'interpersonal_eur_rate';
const LS_EUR_TS   = 'interpersonal_eur_ts';

/* Fetch curs BT live — cu cache 1h în localStorage ─────────────────────── */
async function fetchEurRate(): Promise<number> {
  try {
    const cachedRate = localStorage.getItem(LS_EUR_RATE);
    const cachedTs   = parseInt(localStorage.getItem(LS_EUR_TS) || '0', 10);
    const oneHour    = 60 * 60 * 1000;
    if (cachedRate && Date.now() - cachedTs < oneHour) {
      return parseFloat(cachedRate);
    }

    /* Proxy prin CORS — în producție înlocuiți cu endpoint PHP propriu */
    const res = await fetch('https://www.bancatransilvania.ro/curs-valutar', {
      headers: { Accept: 'text/html' },
      signal: AbortSignal.timeout(5000),
    });
    const html = await res.text();

    /* Extrage cursul VINDEM EUR din HTML BT */
    const match = html.match(/EUR[\s\S]*?(\d+\.\d{4})\s*<\/td>\s*<td[^>]*>\s*(\d+\.\d{4})/);
    let sellRate: number;
    if (match && match[2]) {
      sellRate = parseFloat(match[2]);
    } else {
      /* Fallback la valoarea hardcodată dacă parsing-ul eșuează */
      sellRate = BT_SELL_EUR;
    }

    const finalRate = parseFloat((sellRate * EUR_MARKUP).toFixed(4));
    localStorage.setItem(LS_EUR_RATE, String(finalRate));
    localStorage.setItem(LS_EUR_TS,   String(Date.now()));
    return finalRate;
  } catch {
    /* Fallback fără a sparge aplicația */
    return EUR_TO_RON;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartProduct[]>(() => {
    try { return JSON.parse(localStorage.getItem(LS_CART) || '[]'); } catch { return []; }
  });
  const [eurRate, setEurRate] = useState<number>(
    parseFloat(localStorage.getItem(LS_EUR_RATE) || String(EUR_TO_RON))
  );

  /* Persistare coș */
  useEffect(() => { localStorage.setItem(LS_CART, JSON.stringify(items)); }, [items]);

  /* Fetch curs la montare (max o dată pe oră) */
  useEffect(() => {
    fetchEurRate().then(rate => setEurRate(rate));
  }, []);

  const addToCart = (product: Omit<CartProduct, 'quantity'>) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart  = (id: string) => setItems(prev => prev.filter(i => i.id !== id));
  const updateQuantity  = (id: string, qty: number) => {
    if (qty <= 0) { removeFromCart(id); return; }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity: qty } : i));
  };
  const updateItem = (id: string, updates: Partial<CartProduct>) =>
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
  const clearCart  = () => setItems([]);
  const isInCart   = (id: string) => items.some(i => i.id === id);

  return (
    <CartContext.Provider value={{
      cart: calcState(items, eurRate),
      addToCart, removeFromCart, updateQuantity, updateItem, clearCart, isInCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
